import type { Content } from "@amzn/ads-discovery-theme-service-typescript-client";
import type { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import type { TemplateMetadata } from "@amzn/d16-g-client-deck-builder-typescript-client";
import {
    Flex,
    Link,
    Text,
    TextButton,
    Heading,
    SectionExpander,
    Button,
    Popover,
} from "@amzn/storm-ui";
import { isUndefined, startCase } from "lodash";
import pluralize from "pluralize";
import React from "react";
import { isTruthy } from "remeda";
import styled from "styled-components";
import { useStore } from "zustand";

import { G4sPlacement } from "../../constants/g4sPlacement";
import { TELEMETRY_ID } from "../../constants/telemetry";
import { csatFeedbackStore } from "../../store/csatFeedback";
import { AquilaThumbsFeedbackPlacement } from "../../types/aquila";
import type { CardDetail } from "../../types/card";
import { CardDetailsSource } from "../../types/card";
import type { DownloadAsset } from "../../types/common";
import { InsightPreviewFileFormats } from "../../types/insightPreviewFileFormats";
import { downloadFileWithCustomName } from "../../utils/download.utils";
import { getMarketingContentAndPitchUrl } from "../../utils/downloadUrl.utils";
import {
    logWithTakt,
    getUnifiedFilterTelemetryDetails,
} from "../../utils/telemetry.utils";
import { isWorkbenchActionDisabled } from "../../utils/workbench.utils";
import { SidePanelReportIssue } from "../aquilaReportIssue/SidePanelReportIssue";
import { AquilaThumbsFeedback } from "../aquilaThumbsFeedback/AquilaThumbsFeedback";
import { DeckDecorationWithLanguageModalButton } from "../createDeckDecoration/DeckDecorationWithLanguageModalButton";
import { CardDetails } from "../discovery/insights/insightDetails/CardDetails";
import {
    FooterSkeletonLoader,
    HeaderSkeletonLoader,
    PdfContainerSkeletonLoader,
} from "../discovery/insights/insightDetails/layout";
import {
    StyledDetailSheetDetailsContainer,
    StyledDetailSheetTitleContainer,
} from "../discovery/insights/insightDetails/styles";

import { AISummarySection } from "./AISummarySection";
import { LabelledTextField } from "./LabelledTextField";
import { ModalTextButton } from "./ModalTextButton";
import {
    DivFlexAlignCenter,
    DivFlexSpaceBetween,
    Markdown,
} from "./styled.components";

const VISIBLE_TEMPLATE_FIELD_KEYS = new Set(["description", "methodology"]);

const MARKDOWN_FIELD_KEYS = new Set(["description", "methodology"]);

const StyledDownloadButton = styled(Button)`
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
`;

const StyledDropdownButton = styled(Button)`
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    margin-left: 2px;
    padding: 0 4px;
`;

const StyledSelectAllContainer = styled.div`
    margin-top: 1rem;
`;

export const Header: React.FunctionComponent<{
    numPages: number;
    children?: React.ReactNode;
    identifier?: string;
    insightType?: InsightType;
    title?: string;
}> = ({ numPages, children, identifier, insightType, title }) => {
    return (
        <DivFlexSpaceBetween data-testid="insight-detail-header-num-slides">
            <Heading renderAs="h5">
                {title || "Insight"} &nbsp;&nbsp;
                <Text color={"tertiary"} inline={true}>
                    {pluralize("slide", numPages, true)}
                </Text>
                {children}
            </Heading>
        </DivFlexSpaceBetween>
    );
};

export const HeaderTitle: React.FunctionComponent<{
    title: string;
}> = ({ title }) => (
    <StyledDetailSheetTitleContainer data-testid="insight-detail-header-title">
        <Heading renderAs="h5" size="small" typeface="ember">
            {title}
        </Heading>
    </StyledDetailSheetTitleContainer>
);

export const HeaderCardDetails: React.FunctionComponent<{
    details: CardDetail[];
    templateInfo?: TemplateMetadata | undefined;
    content?: Content;
    selectedSlides?: Set<number>;
    setSelectedSlides?: (selectedSlides: Set<number>) => void;
    numPages?: number;
    previewAsset?: DownloadAsset;
    identifier?: string;
    insightType?: InsightType;
    previewFileFormat?: string;
    downloadAsset?: DownloadAsset;
    isCompliant?: boolean;
    outputFileFormats?: string[];
    buttonOverride?: React.ReactNode[];
    aiSummaryOverview?: string;
    aiSummaryExecutive?: string;
}> = ({
    details,
    templateInfo,
    selectedSlides,
    setSelectedSlides,
    numPages,
    previewAsset,
    identifier,
    insightType,
    previewFileFormat,
    downloadAsset,
    isCompliant,
    outputFileFormats,
    buttonOverride,
    aiSummaryOverview,
    aiSummaryExecutive,
}) => {
    const [isDetailsOpen, setDetailsOpen] = React.useState(false);
    const [isTemplateDetailsOpen, setIsTemplateDetailsOpen] =
        React.useState(false);

    const workbenchDisabled = isWorkbenchActionDisabled(
        isCompliant,
        previewAsset,
        downloadAsset,
        outputFileFormats,
    );

    React.useEffect(() => {
        if (aiSummaryOverview || aiSummaryExecutive) {
            logWithTakt(TELEMETRY_ID.AI_SUMMARY_DROPDOWN_CLICK, {
                action: "show",
                contentId: identifier,
                contentType: insightType,
            });
        }
    }, []);

    return (
        <StyledDetailSheetDetailsContainer data-testid="insight-detail-header-card-details">
            <AISummarySection
                aiSummaryOverview={aiSummaryOverview}
                aiSummaryExecutive={aiSummaryExecutive}
                contentId={identifier}
                contentType={insightType}
                logExpandClick
            />
            <StyledDetailSheetDetailsContainer>
                <SectionExpander
                    label="Details"
                    isOpen={isDetailsOpen}
                    handleClick={() => setDetailsOpen(!isDetailsOpen)}
                >
                    <Flex direction="column" rowGap="xxxsmall">
                        <CardDetails
                            details={details}
                            source={CardDetailsSource.SIDEPANEL}
                        />
                    </Flex>
                </SectionExpander>
                <HeaderExpandableTemplateDetails
                    templateInfo={templateInfo}
                    isOpen={isTemplateDetailsOpen}
                    handleToggle={() =>
                        setIsTemplateDetailsOpen(!isTemplateDetailsOpen)
                    }
                />
            </StyledDetailSheetDetailsContainer>
            {(previewFileFormat === InsightPreviewFileFormats.PDF ||
                previewFileFormat === InsightPreviewFileFormats.FORGE) &&
                identifier &&
                insightType && (
                    <StyledSelectAllContainer>
                        <Flex
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <div>
                                {selectedSlides &&
                                    setSelectedSlides &&
                                    numPages !== undefined &&
                                    numPages > 0 &&
                                    !workbenchDisabled && (
                                        <Text>
                                            {selectedSlides.size ===
                                            numPages ? (
                                                <TextButton
                                                    onClick={() =>
                                                        setSelectedSlides(
                                                            new Set<number>(),
                                                        )
                                                    }
                                                    data-testid="header-unselect-all-slides"
                                                >
                                                    Unselect all slides
                                                </TextButton>
                                            ) : (
                                                <TextButton
                                                    onClick={() =>
                                                        setSelectedSlides(
                                                            new Set([
                                                                ...Array(
                                                                    numPages,
                                                                ).keys(),
                                                            ]),
                                                        )
                                                    }
                                                    data-testid="header-select-all-slides"
                                                >
                                                    Select all slides
                                                </TextButton>
                                            )}
                                        </Text>
                                    )}
                            </div>
                            <DivFlexAlignCenter>
                                {buttonOverride ? (
                                    buttonOverride
                                ) : (
                                    <DeckDecorationWithLanguageModalButton
                                        documentUrl={previewAsset?.downloadUrl}
                                        identifier={identifier}
                                        insightType={insightType}
                                        placement={G4sPlacement.SIDE_PANEL}
                                    />
                                )}
                            </DivFlexAlignCenter>
                        </Flex>
                    </StyledSelectAllContainer>
                )}
        </StyledDetailSheetDetailsContainer>
    );
};

export const HeaderTemplateDetailField: React.FunctionComponent<{
    label: string;
    value: string;
}> = ({ label, value }) => {
    if (MARKDOWN_FIELD_KEYS.has(label))
        return (
            <>
                <Text>
                    <b>{`${startCase(label)}: `}</b>
                </Text>
                <Markdown>{value}</Markdown>
            </>
        );
    return <LabelledTextField label={startCase(label)} value={value} />;
};

const HeaderTemplateDetails: React.FunctionComponent<{
    templateInfo: TemplateMetadata;
}> = ({ templateInfo }) => {
    return (
        <>
            {Object.entries(templateInfo)
                .filter(
                    ([k, v]) =>
                        VISIBLE_TEMPLATE_FIELD_KEYS.has(k) && isTruthy(v),
                )
                .map(([key, value]) => (
                    <HeaderTemplateDetailField
                        key={key}
                        label={key}
                        value={value}
                    />
                ))}
        </>
    );
};

export const HeaderFullTemplateDetails: React.FunctionComponent<{
    templateInfo: TemplateMetadata | undefined;
}> = ({ templateInfo }) => {
    return (
        <div data-testid="insight-detail-header-full-template-details">
            <Flex direction="column" rowGap="xxxsmall">
                {Object.entries(templateInfo || {})
                    .filter(([_, v]) => isTruthy(v))
                    .map(([key, value]) => (
                        <HeaderTemplateDetailField
                            key={key}
                            label={key}
                            value={value}
                        />
                    ))}
            </Flex>
        </div>
    );
};

const HeaderFullTemplateDetailsButton: React.FunctionComponent<{
    templateInfo: TemplateMetadata;
}> = ({ templateInfo }) => {
    const containsAdditionalFields = Object.entries(templateInfo).some(
        ([k, v]) => !VISIBLE_TEMPLATE_FIELD_KEYS.has(k) && isTruthy(v),
    );
    if (!containsAdditionalFields) return null;
    return (
        <ModalTextButton
            buttonLabel="See full details..."
            modalHeader="Full Template Details"
        >
            <HeaderFullTemplateDetails templateInfo={templateInfo} />
        </ModalTextButton>
    );
};

const HeaderExpandableTemplateDetails: React.FunctionComponent<{
    templateInfo: TemplateMetadata | undefined;
    isOpen: boolean;
    handleToggle: () => void;
}> = ({ templateInfo, isOpen, handleToggle }) => {
    if (!templateInfo) return null;

    const containsVisibleFields = Object.entries(templateInfo).some(
        ([k, v]) => VISIBLE_TEMPLATE_FIELD_KEYS.has(k) && isTruthy(v),
    );

    return (
        <SectionExpander
            label="Description and Methodology"
            isOpen={isOpen}
            handleClick={handleToggle}
        >
            <Flex
                direction="column"
                rowGap="xxxsmall"
                data-testid="insight-detail-header-template-details"
            >
                {containsVisibleFields ? (
                    <HeaderTemplateDetails templateInfo={templateInfo} />
                ) : (
                    <Text>No description or methodology found.</Text>
                )}
                <HeaderFullTemplateDetailsButton templateInfo={templateInfo} />
            </Flex>
        </SectionExpander>
    );
};

export const IcmBusinesslineConfiguration: React.FC<{
    icmVersion: string;
    businesslineId: string;
}> = (props) => {
    const { icmVersion, businesslineId } = props;
    const onclickHandler = () => {
        const url = `/icm/view-business-line/${businesslineId}/version/${icmVersion}`;
        window.open(url, "_blank");
    };
    return (
        <Text>
            <Link onClick={onclickHandler} external>
                View Businessline Configuration
            </Link>
        </Text>
    );
};

export const FooterDownloadButton: React.FunctionComponent<{
    handleDownload: () => void;
    downloadAsset: DownloadAsset | undefined;
    identifier?: string;
    insightType?: InsightType;
    showDropdown?: boolean;
    loading?: boolean;
    title?: string;
}> = ({
    handleDownload,
    downloadAsset,
    identifier,
    insightType,
    showDropdown = true,
    loading = false,
    title,
}) => {
    const isDisabled = isUndefined(downloadAsset) || !downloadAsset.downloadUrl;
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const [setIsEligibleToProvideFeedback] = useStore(
        csatFeedbackStore,
        (state) => [state.setHasUserPerformedActionWorthFeedback],
    );

    const handleDirectDownload = async () => {
        if (identifier && insightType) {
            const filters = getUnifiedFilterTelemetryDetails();
            logWithTakt(TELEMETRY_ID.INSIGHT_DOWNLOAD, {
                ...filters,
                insightType,
                identifier,
            });
        }

        const downloadUrl = await getMarketingContentAndPitchUrl(
            insightType!,
            downloadAsset?.contentId,
            downloadAsset?.contentVersion,
            downloadAsset?.downloadUrl,
        );

        // Download file with title as filename
        const urlExtension = downloadUrl.match(/\.([^.?]+)(\?|$)/)?.[1] || "";

        if (title) {
            await downloadFileWithCustomName(downloadUrl, title, urlExtension);
        } else {
            window.open(downloadUrl, "_blank");
        }

        setIsEligibleToProvideFeedback(true);
    };

    if (!showDropdown) {
        return (
            <Button
                onClick={() => void handleDirectDownload()}
                disabled={isDisabled}
                data-testid="insight-detail-download-button"
                loading={loading}
            >
                Download
            </Button>
        );
    }

    return (
        <Flex direction="row">
            <StyledDownloadButton
                onClick={() => void handleDirectDownload()}
                disabled={isDisabled}
                data-testid="insight-detail-download-button"
                loading={loading}
            >
                Download
            </StyledDownloadButton>
            <Popover
                isOpen={isPopoverOpen}
                position="top"
                padding="xxxsmall"
                trigger={
                    <StyledDropdownButton
                        onClick={() => setIsPopoverOpen(!isPopoverOpen)}
                        disabled={isDisabled}
                        aria-label="More download options"
                        aria-haspopup="true"
                        data-testid="insight-detail-download-options-button"
                    >
                        ⋯
                    </StyledDropdownButton>
                }
            >
                <Button
                    variant="transparent"
                    onClick={() => {
                        handleDownload();
                        setIsPopoverOpen(false);
                    }}
                    loading={loading}
                >
                    Download and associate to Salesforce
                </Button>
            </Popover>
        </Flex>
    );
};

export const HeaderComponentBuilder: React.FunctionComponent<{
    loading: boolean;
    headerComponents: JSX.Element[];
}> = ({ loading, headerComponents }) => (
    <div data-testid="header-component-builder">
        {loading ? (
            <HeaderSkeletonLoader />
        ) : (
            <>
                {headerComponents.map((component, index) => (
                    <div key={index}>{component}</div>
                ))}
            </>
        )}
    </div>
);

export const BodyComponentBuilder: React.FunctionComponent<{
    loading: boolean;
    bodyComponents: JSX.Element[];
}> = ({ loading, bodyComponents }) => (
    <div data-testid="body-component-builder">
        {loading ? (
            <PdfContainerSkeletonLoader />
        ) : (
            <>
                {bodyComponents.map((component, index) => (
                    <div key={index}>{component}</div>
                ))}
            </>
        )}
    </div>
);

export const FooterComponentBuilder: React.FunctionComponent<{
    loading: boolean;
    handleClose: () => void;
    footerComponents: JSX.Element[];
    identifier: string | undefined;
    insightType: InsightType | undefined;
}> = ({ loading, handleClose, footerComponents, identifier, insightType }) => {
    return (
        <div data-testid="footer-component-builder">
            {loading ? (
                <FooterSkeletonLoader />
            ) : (
                <Flex justifyContent="space-between" alignItems="center">
                    <Flex columnGap="small">
                        {identifier && insightType && (
                            <AquilaThumbsFeedback
                                identifier={identifier}
                                insightType={insightType}
                                placement={AquilaThumbsFeedbackPlacement.FOOTER}
                                data-testid="aquila-thumbs-feedback"
                            />
                        )}
                        <SidePanelReportIssue data-testid="side-panel-report-issue" />
                    </Flex>
                    <DivFlexAlignCenter>
                        {footerComponents.map((component, index) => (
                            <div key={index}>{component}</div>
                        ))}
                    </DivFlexAlignCenter>
                </Flex>
            )}
        </div>
    );
};
